import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const baseInput =
  "w-full bg-white border border-[#D5CABD] rounded-sm px-4 py-3 text-[15px] " +
  "text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#7A2E39] " +
  "focus:ring-2 focus:ring-[#7A2E39]/15 transition";

export default function ContactForm({ defaultSubject = "Demande d'expertise", compact = false }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    subject: defaultSubject,
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      toast.success("Demande envoyée. Nous vous rappelons rapidement.", {
        description: data.email_sent
          ? "Un accusé a été envoyé à notre équipe."
          : "Votre demande est enregistrée, nous revenons vers vous.",
      });
      setForm({ name: "", phone: "", email: "", city: "", subject: defaultSubject, message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue.", {
        description:
          err?.response?.data?.detail ||
          "Merci de réessayer ou de nous appeler directement au 07 45 71 15 10.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white p-6 sm:p-8 rounded-sm border border-[#D5CABD] shadow-sm"
      data-testid="contact-form"
    >
      <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"} gap-4`}>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1.5 block">
            Nom & prénom *
          </label>
          <input
            className={baseInput}
            value={form.name}
            onChange={update("name")}
            required
            data-testid="contact-name"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1.5 block">
            Téléphone *
          </label>
          <input
            className={baseInput}
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            required
            data-testid="contact-phone"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1.5 block">
            Email *
          </label>
          <input
            className={baseInput}
            type="email"
            value={form.email}
            onChange={update("email")}
            required
            data-testid="contact-email"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1.5 block">
            Ville
          </label>
          <input
            className={baseInput}
            value={form.city}
            onChange={update("city")}
            placeholder="Bordeaux, Mérignac…"
            data-testid="contact-city"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1.5 block">
            Sujet
          </label>
          <input
            className={baseInput}
            value={form.subject}
            onChange={update("subject")}
            data-testid="contact-subject"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1.5 block">
            Message *
          </label>
          <textarea
            className={baseInput + " min-h-[140px] resize-y"}
            value={form.message}
            onChange={update("message")}
            placeholder="Décrivez votre situation : nature du bâti, signes observés, adresse approximative…"
            required
            data-testid="contact-message"
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="btn-primary mt-6 w-full sm:w-auto disabled:opacity-70"
        data-testid="submit-contact-form"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {loading ? "Envoi en cours…" : "Envoyer ma demande"}
      </button>
      <p className="text-xs text-gray-500 mt-3">
        Vos données sont utilisées uniquement pour répondre à votre demande. Aucun partenariat commercial.
      </p>
    </form>
  );
}
